
import { useState, useEffect, useCallback } from "react";
import { AIStaffMember } from "@/lib/types";
import { logger } from "@/lib/logger";
import { useStaffIdValidator } from "./useStaffIdValidator";
import { userApiClient, businessApiClient, staffApiClient } from "@/api";

export function useSingleStaff() {
  const [aiStaff, setAiStaff] = useState<AIStaffMember | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  
  const { validateStaffId } = useStaffIdValidator();

  const fetchAIStaff = useCallback(async (staffId?: string) => {
    // Validate the staff ID format
    if (staffId && !validateStaffId(staffId)) {
      setError(new Error("Invalid staff ID format"));
      setIsLoading(false);
      return null;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      const userId = await userApiClient.getCurrentUser();
      if (!userId) {
        logger.error({
          message: "No authenticated user found",
          context: { staffId }
        });
        return null;
      }
      
      const business = await businessApiClient.fetchBusinessByOwnerId(userId);
      if (!business) {
        logger.error({
          message: "No business found for user",
          context: { userId, staffId }
        });
        return null;
      }
      
      if (staffId) {
        // Fetch a specific staff member
        const staffData = await staffApiClient.fetchStaffById(business.id, staffId);
        setAiStaff(staffData);
        return staffData;
      } else {
        // Fetch the first active staff member
        const staffDataArray = await staffApiClient.fetchAllStaff(business.id);
        const firstStaff = staffDataArray.length > 0 ? staffDataArray[0] : null;
        setAiStaff(firstStaff);
        return firstStaff;
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      logger.error({
        message: "Exception fetching AI staff",
        error,
        context: { staffId }
      });
      
      setError(error);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [validateStaffId]);

  const clearCache = useCallback(() => {
    staffApiClient.clearCache();
  }, []);

  return {
    aiStaff,
    setAiStaff,
    fetchAIStaff,
    isLoading,
    error,
    clearCache
  };
}
